import styled from "@emotion/native"
import { Spacing, Text } from ".."
import { TransactionsApiTransaction } from "../../api/queries"
import { RefreshControl, SectionList } from "react-native"
import { useEffect, useState } from "react"
import { TransactionListItem } from "."

type TransactionListProps = {
  transactions: TransactionsApiTransaction[] | undefined
  onPress: (id: string) => void
  onRefresh?: () => void
  isFetching?: boolean
  /** Extra bottom padding for scroll content (e.g. above a tab bar). */
  contentBottomPadding?: number
  /** When false, RefreshControl is not mounted (avoids stuck iOS spinner when the list is off-screen). */
  refreshControlActive?: boolean
}

type TransactionSection = {
  title: string
  data: TransactionItem[]
}

type TransactionItem = {
  id: string
  amount: { value: number; currencyCode: string }
  name: string
  accountFourDigits: string
  isRefunded: boolean
  isReimbursed: boolean
  paymentMethod: "A2A" | "Card"
}

export function TransactionList(props: TransactionListProps) {
  const {
    transactions,
    onPress,
    onRefresh,
    isFetching,
    contentBottomPadding = 0,
    refreshControlActive = true,
  } = props
  const [transactionList, setTransactionList] = useState<TransactionSection[]>([])

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh()
    }
  }

  useEffect(() => {
    if (transactions) {
      const result = transactions.reduce<TransactionSection[]>((sections, transaction) => {
        const sectionTitle = new Date(transaction.date).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
        })
        const transactionForSection = {
          id: transaction.externalTransferId,
          name: transaction.recipientName,
          amount: transaction.amount,
          accountFourDigits: transaction.account,
          isRefunded: transaction.isRefunded,
          isReimbursed: transaction.isReimbursed,
          paymentMethod: transaction.paymentMethod,
        }
        if (sections.some((s) => s.title === sectionTitle)) {
          const index = sections.findIndex((s) => s.title === sectionTitle)
          sections[index].data.push(transactionForSection)
        } else {
          sections.push({ title: sectionTitle, data: [transactionForSection] })
        }
        return sections
      }, [])
      setTransactionList(result)
    }
  }, [transactions])

  return (
    <ListContainer>
      <SectionList
        sections={transactionList}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <TransactionListItem
            id={item.id}
            name={item.name}
            amount={item.amount}
            accountFourDigits={item.accountFourDigits}
            isReimbursed={item.isReimbursed}
            logoColor="lightgray"
            paymentMethod={item.paymentMethod}
            onPress={onPress}
          />
        )}
        renderSectionHeader={({ section: { title } }) => (
          <SectionHeader>
            <Text type="sectionTitle" color="tertiary">
              {title}
            </Text>
          </SectionHeader>
        )}
        refreshControl={
          refreshControlActive ? (
            <RefreshControl refreshing={!!isFetching} onRefresh={handleRefresh} />
          ) : undefined
        }
        ItemSeparatorComponent={() => <Spacing top={24} />}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: contentBottomPadding }}
      />
    </ListContainer>
  )
}

const ListContainer = styled.View``

const SectionHeader = styled.View`
  padding-bottom: 8px;
  margin-bottom: 17px;
  margin-top: 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.text.tertiary};
`
